/* Zaineer product page (sections/zaineer-pdp.liquid)
 * - variant buttons: price, SKU, URL, image and button state
 * - add to cart (uses the theme cart popup when present, otherwise goes to /cart)
 * - gallery: thumbnails (desktop), swipe + dots (mobile), image viewer with zoom
 * - collapsible tabs, size chart dialog, wishlist heart
 */
(() => {
  'use strict';

  if (window.__zaineerPdp) return;
  window.__zaineerPdp = true;

  const WISH_KEY = 'bls__wishlist-items';
  const MOBILE = window.matchMedia('(max-width: 768px)');

  const $ = (root, sel) => root.querySelector(sel);
  const $$ = (root, sel) => Array.prototype.slice.call(root.querySelectorAll(sel));

  const ICONS = {
    close:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg>',
    prev:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 4 7 12l8 8"/></svg>',
    next:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 4 8 8-8 8"/></svg>'
  };

  /* ---------------- money ---------------- */
  function formatAmount(cents, decimals) {
    const fixed = (Number(cents || 0) / 100).toFixed(decimals);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  function formatMoney(cents, root) {
    if (root.dataset.priceFormat === 'inr') {
      const amount = formatAmount(cents, 2).replace(/\.00$/, '');
      const prefix = root.dataset.pricePrefix || '';
      return prefix ? prefix + ' ' + amount : amount;
    }
    const format = root.dataset.moneyFormat || '{{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return formatAmount(cents, 0);
        case 'amount_with_comma_separator':
          return formatAmount(cents, 2).replace(/,/g, '#').replace('.', ',').replace(/#/g, '.');
        case 'amount_no_decimals_with_comma_separator':
          return formatAmount(cents, 0).replace(/,/g, '.');
        case 'amount_with_apostrophe_separator':
          return formatAmount(cents, 2).replace(/,/g, "'");
        default:
          return formatAmount(cents, 2);
      }
    });
  }

  /* ---------------- wishlist (shared with the theme's wishlist page) ---------------- */
  const Wishlist = {
    read() {
      try {
        const list = JSON.parse(window.localStorage.getItem(WISH_KEY));
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        window.localStorage.setItem(WISH_KEY, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable */
      }
      $$(document, '.wishlist-count').forEach((el) => {
        el.textContent = list.length;
      });
    },
    has(handle) {
      return this.read().indexOf(handle) > -1;
    },
    toggle(handle) {
      const list = this.read();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1);
      else list.push(handle);
      this.write(list);
    }
  };

  /* ---------------- image viewer ---------------- */
  const Viewer = {
    el: null,
    stage: null,
    img: null,
    count: null,
    items: [],
    index: 0,
    trigger: null,
    startX: 0,
    startY: 0,

    build() {
      if (this.el) return;
      const el = document.createElement('div');
      el.className = 'zrp-lightbox';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Product images');
      el.innerHTML =
        '<div class="zrp-lightbox__stage"><img class="zrp-lightbox__img" alt=""></div>' +
        '<button type="button" class="zrp-lightbox__btn zrp-lightbox__close" aria-label="Close">' + ICONS.close + '</button>' +
        '<button type="button" class="zrp-lightbox__btn zrp-lightbox__prev" aria-label="Previous image">' + ICONS.prev + '</button>' +
        '<button type="button" class="zrp-lightbox__btn zrp-lightbox__next" aria-label="Next image">' + ICONS.next + '</button>' +
        '<div class="zrp-lightbox__count" aria-live="polite"></div>';
      document.body.appendChild(el);
      this.el = el;
      this.stage = $(el, '.zrp-lightbox__stage');
      this.img = $(el, '.zrp-lightbox__img');
      this.count = $(el, '.zrp-lightbox__count');

      $(el, '.zrp-lightbox__close').addEventListener('click', () => this.close());
      $(el, '.zrp-lightbox__prev').addEventListener('click', () => this.show(this.index - 1));
      $(el, '.zrp-lightbox__next').addEventListener('click', () => this.show(this.index + 1));

      this.stage.addEventListener('click', (e) => this.toggleZoom(e));
      this.stage.addEventListener('mousemove', (e) => this.pan(e));

      this.stage.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          this.startX = e.touches[0].clientX;
          this.startY = e.touches[0].clientY;
        },
        { passive: true }
      );
      this.stage.addEventListener(
        'touchend',
        (e) => {
          if (this.el.classList.contains('is-zoomed') || !e.changedTouches.length) return;
          const dx = e.changedTouches[0].clientX - this.startX;
          const dy = e.changedTouches[0].clientY - this.startY;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            this.swiped = true;
            this.show(this.index + (dx < 0 ? 1 : -1));
          }
        },
        { passive: true }
      );

      document.addEventListener('keydown', (e) => {
        if (!this.el || !this.el.classList.contains('is-open')) return;
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowLeft') this.show(this.index - 1);
        else if (e.key === 'ArrowRight') this.show(this.index + 1);
      });
    },

    open(items, index, trigger) {
      if (!items.length) return;
      this.build();
      this.items = items;
      this.trigger = trigger || null;
      this.el.classList.toggle('is-single', items.length < 2);
      this.el.classList.add('is-open');
      document.documentElement.classList.add('zrp-lock');
      this.show(index);
      $(this.el, '.zrp-lightbox__close').focus({ preventScroll: true });
    },

    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open', 'is-zoomed');
      document.documentElement.classList.remove('zrp-lock');
      this.img.removeAttribute('src');
      if (this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    },

    show(i) {
      const total = this.items.length;
      if (!total) return;
      this.index = (i + total) % total;
      this.el.classList.remove('is-zoomed');
      const item = this.items[this.index];
      this.img.src = item.src;
      this.img.alt = item.alt || '';
      this.count.textContent = this.index + 1 + ' / ' + total;
    },

    toggleZoom(e) {
      if (this.swiped) {
        this.swiped = false;
        return;
      }
      const el = this.el;
      if (el.classList.contains('is-zoomed')) {
        el.classList.remove('is-zoomed');
        return;
      }
      const nw = this.img.naturalWidth;
      const nh = this.img.naturalHeight;
      if (!nw || !nh) return;
      const rect = this.img.getBoundingClientRect();
      const rx = rect.width ? Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0.5;
      const ry = rect.height ? Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)) : 0.5;
      const sw = this.stage.clientWidth;
      const sh = this.stage.clientHeight;
      const width = Math.max(nw, sw * 1.6, ((sh * 1.6) * nw) / nh);
      el.style.setProperty('--zrp-zw', Math.round(width) + 'px');
      el.classList.add('is-zoomed');
      const height = (width * nh) / nw;
      this.stage.scrollLeft = rx * (width - sw);
      this.stage.scrollTop = ry * (height - sh);
    },

    pan(e) {
      if (!this.el.classList.contains('is-zoomed') || !window.matchMedia('(pointer: fine)').matches) return;
      const sw = this.stage.clientWidth;
      const sh = this.stage.clientHeight;
      this.stage.scrollLeft = (e.clientX / sw) * (this.stage.scrollWidth - sw);
      this.stage.scrollTop = (e.clientY / sh) * (this.stage.scrollHeight - sh);
    }
  };

  /* ---------------- product section ---------------- */
  class ZaineerProduct {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.handle = root.dataset.productHandle;

      const json = $(root, '[data-zrp-variants]');
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      this.idInput = $(root, '[data-zrp-variant-id]');
      this.form = this.idInput ? this.idInput.form : null;
      this.atc = $(root, '[data-zrp-atc]');
      this.atcText = $(root, '[data-zrp-atc-text]');
      this.error = $(root, '[data-zrp-error]');
      this.groups = $$(root, '[data-zrp-option]');

      this.initGallery();
      this.initOptions();
      this.initForm();
      this.initTabs();
      this.initWishlist();
      this.initChart();
    }

    /* ----- gallery ----- */
    initGallery() {
      const root = this.root;
      this.slides = $$(root, '[data-zrp-slide]');
      this.thumbs = $$(root, '[data-zrp-thumb]');
      this.dots = $$(root, '[data-zrp-dot]');
      this.track = $(root, '[data-zrp-slides]');
      this.thumbScroller = $(root, '[data-zrp-thumbs]');

      const active = this.slides.findIndex((s) => s.classList.contains('is-active'));
      this.index = active > -1 ? active : 0;

      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i, true)));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i, true)));

      if (this.track) {
        let ticking = false;
        this.track.addEventListener(
          'scroll',
          () => {
            if (!MOBILE.matches || ticking) return;
            ticking = true;
            window.requestAnimationFrame(() => {
              ticking = false;
              const width = this.track.clientWidth || 1;
              const i = Math.round(this.track.scrollLeft / width);
              if (i !== this.index && this.slides[i]) this.setActive(i, false);
            });
          },
          { passive: true }
        );
      }

      const onBreakpoint = () => this.goTo(this.index, false);
      if (MOBILE.addEventListener) MOBILE.addEventListener('change', onBreakpoint);
      else if (MOBILE.addListener) MOBILE.addListener(onBreakpoint);

      let resizeTimer = null;
      window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => this.updateTrackHeight(), 120);
      });
      this.slides.forEach((slide) => {
        const img = slide.querySelector('img');
        if (img && !img.complete) img.addEventListener('load', () => this.updateTrackHeight(), { once: true });
      });
      this.updateTrackHeight();

      if (root.dataset.zoom === 'true') {
        this.slides.forEach((slide, i) => {
          if (slide.dataset.mediaType !== 'image') return;
          slide.addEventListener('click', (e) => {
            if (e.target.closest('button, a')) return;
            this.openViewer(i, slide);
          });
        });
        const zoomBtn = $(root, '[data-zrp-zoom]');
        if (zoomBtn) zoomBtn.addEventListener('click', () => this.openViewer(this.index, zoomBtn));
      }

      if (MOBILE.matches && this.index > 0) {
        window.requestAnimationFrame(() => this.goTo(this.index, false));
      }
    }

    setActive(i, animate) {
      const changed = i !== this.index;
      this.index = i;
      this.slides.forEach((slide, n) => {
        const on = n === i;
        slide.classList.toggle('is-active', on);
        if (on && changed && animate) {
          slide.classList.remove('zrp-fade-in');
          void slide.offsetWidth;
          slide.classList.add('zrp-fade-in');
        }
        if (!on) {
          const video = slide.querySelector('video');
          if (video && !video.paused) video.pause();
        }
      });
      this.thumbs.forEach((thumb, n) => {
        thumb.classList.toggle('is-active', n === i);
        if (n === i) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
      this.updateTrackHeight();
    }

    /* mobile: the swipe area takes the height of the image in view */
    updateTrackHeight() {
      if (!this.track) return;
      if (!MOBILE.matches) {
        this.track.style.height = '';
        return;
      }
      const slide = this.slides[this.index];
      if (slide && slide.offsetHeight) this.track.style.height = slide.offsetHeight + 'px';
    }

    goTo(i, smooth) {
      if (!this.slides[i]) return;
      this.setActive(i, smooth);
      if (MOBILE.matches && this.track) {
        this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      }
      const thumb = this.thumbs[i];
      const scroller = this.thumbScroller;
      if (thumb && scroller && !MOBILE.matches) {
        const top = thumb.offsetTop;
        const bottom = top + thumb.offsetHeight;
        if (top < scroller.scrollTop) {
          scroller.scrollTo({ top: top, behavior: 'smooth' });
        } else if (bottom > scroller.scrollTop + scroller.clientHeight) {
          scroller.scrollTo({ top: bottom - scroller.clientHeight, behavior: 'smooth' });
        }
      }
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (i > -1 && i !== this.index) this.goTo(i, true);
    }

    openViewer(i, trigger) {
      const items = [];
      let start = 0;
      this.slides.forEach((slide, n) => {
        if (slide.dataset.mediaType !== 'image' || !slide.dataset.zoomSrc) return;
        if (n === i) start = items.length;
        const img = slide.querySelector('img');
        items.push({ src: slide.dataset.zoomSrc, alt: img ? img.alt : '' });
      });
      Viewer.open(items, start, trigger);
    }

    /* ----- options ----- */
    initOptions() {
      if (!this.groups.length) return;
      this.root.addEventListener('change', (e) => {
        if (e.target.matches('[data-zrp-opt-input]')) this.onOptionChange();
      });
      this.updateAvailability(this.getSelected());
    }

    getSelected() {
      return this.groups.map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(selected) {
      return (
        this.variants.find(
          (v) => Array.isArray(v.options) && v.options.length === selected.length && v.options.every((o, i) => o === selected[i])
        ) || null
      );
    }

    onOptionChange() {
      const selected = this.getSelected();
      const variant = this.findVariant(selected);
      this.updateAvailability(selected);
      this.updateButtons(variant);
      if (!variant) return;

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updatePrice(variant);
      this.updateSku(variant);
      if (variant.featured_media) this.goToMedia(variant.featured_media.id);
      this.updateUrl(variant);
      this.root.dispatchEvent(new CustomEvent('zrp:variant-change', { bubbles: true, detail: { variant: variant } }));
    }

    updateAvailability(selected) {
      this.groups.forEach((group, i) => {
        $$(group, '[data-zrp-opt-input]').forEach((input) => {
          const available = this.variants.some(
            (v) =>
              v.available &&
              Array.isArray(v.options) &&
              v.options[i] === input.value &&
              v.options.every((o, j) => j === i || selected[j] == null || o === selected[j])
          );
          const wrap = input.closest('.zrp-value');
          if (wrap) wrap.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updateButtons(variant) {
      if (!this.atc) return;
      const labels = this.atc.dataset;
      let text = labels.labelAdd;
      let disabled = false;
      if (!variant) {
        text = labels.labelUnavailable;
        disabled = true;
      } else if (!variant.available) {
        text = labels.labelSoldOut;
        disabled = true;
      }
      this.atc.disabled = disabled;
      if (this.atcText) this.atcText.textContent = text;
      const buyNow = $(this.root, '.shopify-payment-button');
      if (buyNow) buyNow.classList.toggle('zrp-is-disabled', disabled);
      this.hideError();
    }

    updatePrice(variant) {
      const price = $(this.root, '[data-zrp-price]');
      const compare = $(this.root, '[data-zrp-compare]');
      if (price) price.textContent = formatMoney(variant.price, this.root);
      if (compare) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        compare.hidden = !onSale;
        compare.textContent = onSale ? formatMoney(variant.compare_at_price, this.root) : '';
      }
    }

    updateSku(variant) {
      const sku = $(this.root, '[data-zrp-sku]');
      if (!sku) return;
      sku.textContent = variant.sku || '';
      sku.hidden = !variant.sku;
    }

    updateUrl(variant) {
      if (window.Shopify && window.Shopify.designMode) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) {
        /* ignore */
      }
    }

    /* ----- add to cart ----- */
    initForm() {
      if (!this.form || !this.atc) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (this.atc.disabled || this.atc.classList.contains('is-loading')) return;
        this.hideError();
        this.atc.classList.add('is-loading');
        this.atc.setAttribute('aria-busy', 'true');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = new FormData(this.form);
        let sections = [];
        if (cart && typeof cart.getSectionsToRender === 'function') {
          try {
            sections = cart.getSectionsToRender().map((s) => s.section || s.id).filter(Boolean);
          } catch (err) {
            sections = [];
          }
          if (sections.length) {
            body.append('sections', sections.join(','));
            body.append('sections_url', window.location.pathname);
          }
        }

        const routes = window.routes || {};
        const addUrl = (routes.cart_add_url || '/cart/add') + '.js';
        const cartUrl = routes.cart_url || '/cart';

        fetch(addUrl, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: body
        })
          .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
          .then((result) => {
            const data = result.data || {};
            if (!result.ok || data.status) {
              this.showError(data.description || data.message || 'This item could not be added to your cart.');
              return;
            }
            document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: data } }));
            if (cart && sections.length && data.sections && typeof cart.open === 'function') {
              this.renderSections(data.sections);
              cart.open();
              this.refreshCount();
            } else {
              window.location.href = cartUrl;
            }
          })
          .catch(() => {
            this.form.submit();
          })
          .finally(() => {
            this.atc.classList.remove('is-loading');
            this.atc.removeAttribute('aria-busy');
          });
      });
    }

    renderSections(sections) {
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        const target = document.getElementById(id) || document.getElementById('shopify-section-' + id);
        if (!html || !target) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById(id) || doc.querySelector('.shopify-section');
        target.innerHTML = fresh ? fresh.innerHTML : html;
      });
    }

    refreshCount() {
      fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          $$(document, '.cart-count').forEach((el) => {
            el.textContent = cart.item_count;
          });
        })
        .catch(() => {});
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    /* ----- collapsible tabs ----- */
    initTabs() {
      $$(this.root, '[data-zrp-tab]').forEach((tab) => {
        const button = $(tab, '.zrp-tab__btn');
        const content = $(tab, '.zrp-tab__content');
        const inner = $(tab, '.zrp-tab__inner');
        if (!button || !content || !inner) return;
        button.addEventListener('click', () => this.toggleTab(tab, button, content, inner));
      });
    }

    toggleTab(tab, button, content, inner) {
      const opening = !tab.classList.contains('is-open');
      button.setAttribute('aria-expanded', opening ? 'true' : 'false');
      if (opening) {
        tab.classList.add('is-open');
        content.style.height = inner.offsetHeight + 'px';
        const done = (e) => {
          if (e.target !== content || e.propertyName !== 'height') return;
          content.removeEventListener('transitionend', done);
          if (tab.classList.contains('is-open')) content.style.height = 'auto';
        };
        content.addEventListener('transitionend', done);
      } else {
        content.style.height = content.offsetHeight + 'px';
        void content.offsetHeight;
        tab.classList.remove('is-open');
        content.style.height = '0px';
      }
    }

    /* ----- wishlist ----- */
    initWishlist() {
      const buttons = $$(this.root, '[data-zrp-wish]');
      if (!buttons.length || !this.handle) return;
      const sync = () => {
        const on = Wishlist.has(this.handle);
        buttons.forEach((b) => {
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
        });
      };
      buttons.forEach((b) =>
        b.addEventListener('click', () => {
          Wishlist.toggle(this.handle);
          sync();
        })
      );
      window.addEventListener('storage', (e) => {
        if (e.key === WISH_KEY) sync();
      });
      sync();
    }

    /* ----- size chart dialog ----- */
    initChart() {
      const modal = $(this.root, '[data-zrp-chart-modal]');
      const openers = $$(this.root, '[data-zrp-chart-open]');
      if (!modal || !openers.length) return;

      $$(document, '.zrp-portal[data-zrp-for="' + this.sectionId + '"]').forEach((old) => {
        if (old !== modal) old.remove();
      });
      modal.classList.add('zrp-portal');
      modal.setAttribute('data-zrp-for', this.sectionId);
      document.body.appendChild(modal);

      let lastFocus = null;
      const close = () => {
        modal.hidden = true;
        document.documentElement.classList.remove('zrp-lock');
        if (lastFocus) lastFocus.focus({ preventScroll: true });
      };
      const open = (trigger) => {
        lastFocus = trigger;
        modal.hidden = false;
        document.documentElement.classList.add('zrp-lock');
        const closeBtn = $(modal, '.zrp-modal__close');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      };

      openers.forEach((btn) => btn.addEventListener('click', () => open(btn)));
      $$(modal, '[data-zrp-chart-close]').forEach((btn) => btn.addEventListener('click', close));
      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    }
  }

  /* ---------------- boot ---------------- */
  const instances = new WeakMap();

  function init(scope) {
    $$(scope || document, '[data-zrp]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new ZaineerProduct(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const id = e.detail && e.detail.sectionId;
    if (!id) return;
    $$(document, '.zrp-portal[data-zrp-for="' + id + '"]').forEach((node) => node.remove());
    document.documentElement.classList.remove('zrp-lock');
  });
  document.addEventListener('shopify:block:select', (e) => {
    const tab = e.target && e.target.querySelector ? e.target.querySelector('[data-zrp-tab]') : null;
    if (tab && !tab.classList.contains('is-open')) {
      const button = tab.querySelector('.zrp-tab__btn');
      if (button) button.click();
    }
  });
})();
